/* eslint-disable @typescript-eslint/member-ordering */
import { error, _decorator, sp, Enum, CCFloat, CCInteger, ccenum } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import { setEnumAttr, hasSameEnumEntries, withNoneOption } from '../../Utils/Utility';
import BaseAction from './BaseAction';

const { ccclass, property } = _decorator;

export enum DefaultSkinsEnum {
    '&lt;None&gt;' = 0,
}
ccenum(DefaultSkinsEnum);
export enum DefaultAnimsEnum {
    '<None>' = 0,
}
ccenum(DefaultAnimsEnum);

// 操作行為
export enum SpineAnimationActionType {
    SetOrAddAnimation,
    AppendAnimation,
    /** not included in SetProperty because of compatibility issue */
    SetTimeScale,
    /** no actual method will be invoked, just used to prevent other actions from being executed */
    SetProperty,
}

@ccclass('SpineAnimationAction')
export default class SpineAnimationAction extends BaseAction {
    @property({ type: sp.Skeleton, displayName: DEV && 'Spine元件', tooltip: DEV && 'skeleton' })
    public get displaySkeleton() {
        return this.skeleton;
    }
    public set displaySkeleton(value: sp.Skeleton) {
        this.skeleton = value;
        this._refreshInspector();
    }
    @property({ type: Enum(SpineAnimationActionType), displayName: DEV && '表演類型', tooltip: DEV && '操作行為(actionType)' })
    public readonly actionType: SpineAnimationActionType = SpineAnimationActionType.SetOrAddAnimation;

    @property({
        displayName: DEV && '加速倍率',
        tooltip: DEV && '更改原動畫加速倍率\n恢復原始TimeScale需另外新增Keyframe',
        type: CCFloat,
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetTimeScale;
        },
    })
    public readonly timeScale: number = 1;

    @property({
        displayName: DEV && '動畫名稱',
        tooltip: DEV && '動畫名稱(animationName)',
        type: DefaultAnimsEnum,
        visible(this: SpineAnimationAction) {
            return (
                !!this.skeleton?.skeletonData &&
                (this.actionType === SpineAnimationActionType.SetOrAddAnimation || this.actionType === SpineAnimationActionType.AppendAnimation)
            );
        },
    })
    public get _animationIndex() {
        if (EDITOR) {
            this._syncInspectorEnums();
        }
        const { animationName } = this;
        if (this.skeleton?.skeletonData) {
            if (animationName) {
                const animsEnum = this.skeleton.skeletonData.getAnimsEnum();
                if (!animsEnum) {
                    error(`無法從節點 {${this.skeleton.node.getPathInHierarchy()}} 的 skeletonData 獲取 animsEnum，請檢查 skeletonData 是否丟失。`);
                    return 0;
                }
                const updatedAnimsEnum: { [key: string]: number } = {};

                for (const [key, value] of Object.entries(animsEnum)) {
                    updatedAnimsEnum[key.split('(時長:')[0].slice(0, -1)] = value;
                }
                if (updatedAnimsEnum) {
                    const animIndex = updatedAnimsEnum[animationName];
                    if (animIndex !== undefined) {
                        return animIndex;
                    }
                }
            } else {
                this._refreshInspector();
            }
        }
        return 0;
    }
    public set _animationIndex(value: number) {
        let animsEnum = {};
        if (this.skeleton?.skeletonData) {
            animsEnum = this.skeleton.skeletonData.getAnimsEnum();
        }
        // console.log(animsEnum, value)
        const animName = animsEnum[value];
        if (animName !== undefined) {
            // console.log(animName)
            this.animationName = animName.split('(時長:')[0].slice(0, -1);
            this._refreshInspector();
        }
    }

    @property({
        displayName: DEV && '動畫名稱',
        tooltip: DEV && '動畫名稱(animationName)',
        visible(this: SpineAnimationAction) {
            return (
                !this.skeleton?.skeletonData &&
                (this.actionType === SpineAnimationActionType.SetOrAddAnimation || this.actionType === SpineAnimationActionType.AppendAnimation)
            );
        },
        serializable: true,
    })
    public animationName: string = '';

    @property({
        type: CCInteger,
        displayName: DEV && '動畫軌道編號',
        tooltip: DEV && '動畫軌道(TrackIndex)編號 \n(預設:0, 啟用CacheMode時不適用)',
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetOrAddAnimation;
        },
    })
    public readonly animationTrackIndex: number = 0;
    @property({
        type: CCInteger,
        displayName: DEV && '起始時間',
        tooltip: DEV && '動畫直接從多少毫秒開始 \n(預設:0, 接續動畫不適用)',
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetOrAddAnimation && !this.isAddAnimation;
        },
    })
    public readonly startTime: number = 0;
    @property({
        displayName: DEV && '循環播放',
        tooltip: DEV && '是否為循環播放(loop)',
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetOrAddAnimation || this.actionType === SpineAnimationActionType.AppendAnimation;
        },
    })
    public readonly isLoop: boolean = false;
    @property({
        displayName: DEV && '接續播放',
        tooltip: DEV && '是否接續在當前動畫之後播放 (AddAnimation)\n若當前動畫是循環表演則無法進入此動畫',
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetOrAddAnimation;
        },
    })
    public readonly isAddAnimation: boolean = false;
    @property({
        displayName: DEV && '中斷原有接續播放',
        tooltip: DEV && '是否清空在這之前設定的接續播放 (AddAnimation)',
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetOrAddAnimation;
        },
    })
    public readonly interruptAddAnimation: boolean = false;
    @property({
        displayName: DEV && '加速倍率',
        tooltip: DEV && '原動畫加速倍率\n切換至新動畫後會恢復原始TimeScale',
        type: CCFloat,
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.AppendAnimation;
        },
    })
    public readonly speedUpRate: number = 1;
    @property({
        displayName: DEV && '重置回預備姿勢',
        tooltip: DEV && '是否在動畫開始前重置回預備姿勢',
        visible(this: SpineAnimationAction) {
            return this.actionType === SpineAnimationActionType.SetOrAddAnimation;
        },
    })
    public readonly setToSetupPose: boolean = false;
    @property({
        displayName: DEV && '是否變更Skeleton',
        tooltip: DEV && '是否變更 Spine Skeleton',
        visible(this: SpineAnimationAction) {
            return true;
        },
    })
    public readonly changeSkeleton: boolean = false;
    @property({
        type: sp.SkeletonData,
        visible(this: SpineAnimationAction) {
            return this.changeSkeleton;
        },
    })
    public readonly skeletonData: sp.SkeletonData = null;
    @property({
        displayName: DEV && '是否變更Skin',
        tooltip: DEV && '是否變更 Spine Skin',
        visible(this: SpineAnimationAction) {
            return true;
        },
    })
    public readonly changeSkin: boolean = false;
    @property({
        displayName: DEV && 'Skin名稱',
        tooltip: DEV && 'Spine 的 Skin名稱',
        type: DefaultSkinsEnum,
        visible(this: SpineAnimationAction) {
            return !!this.skeleton?.skeletonData && this.changeSkin;
        },
    })
    public get _skinIndex() {
        if (EDITOR) {
            this._syncInspectorEnums();
        }
        const { skinName } = this;
        if (this.skeleton?.skeletonData) {
            if (skinName) {
                const skinsEnum = this._getSkinEnumSource();
                if (skinsEnum) {
                    const skinIndex = skinsEnum[skinName];
                    if (Number.isInteger(skinIndex)) {
                        return skinIndex;
                    }
                }
            }
            this._refreshInspector();
        }
        return 0;
    }
    public set _skinIndex(value: number) {
        if (value === 0) {
            this.skinName = '';
        } else {
            const skin = this._getSkinEnumSource()?.[value];
            if (skin !== undefined) {
                this.skinName = skin;
            }
        }
        this._refreshInspector();
    }
    @property({
        displayName: DEV && 'Skin名稱',
        tooltip: DEV && 'Spine 的 Skin名稱',
        serializable: true,
        visible(this: SpineAnimationAction) {
            return !this.skeleton?.skeletonData && this.changeSkin;
        },
    })
    public skinName: string = '';

    @property({
        displayName: DEV && '是否設置暫停',
        tooltip: DEV && '是否設置暫停',
    })
    public readonly setPaused: boolean = false;

    @property({
        displayName: DEV && '是否暫停',
        tooltip: DEV && '是否暫停動畫',
        visible(this: SpineAnimationAction) {
            return this.setPaused;
        },
    })
    public readonly paused: boolean = false;

    @property({
        visible: false,
        serializable: true,
    })
    public skeleton: sp.Skeleton = null;

    protected _enumSkins: any = Enum({});
    protected _enumAnimations: any = Enum({});

    private _refreshInspector() {
        this._updateAnimEnum();
        this._updateSkinEnum();
    }

    private _getAnimEnumSource() {
        if (this.skeleton?.skeletonData) {
            return this.skeleton.skeletonData.getAnimsEnum();
        }
        return DefaultAnimsEnum;
    }

    private _updateAnimEnum() {
        const animEnum = this._getAnimEnumSource();
        this._enumAnimations = Enum({});
        Object.assign(this._enumAnimations, animEnum);
        Enum.update(this._enumAnimations);
        setEnumAttr(this, '_animationIndex', this._enumAnimations);
    }

    private _getSkinEnumSource() {
        if (this.skeleton?.skeletonData) {
            return withNoneOption(this.skeleton.skeletonData.getSkinsEnum());
        }
        return DefaultSkinsEnum;
    }

    // update skin list for editor
    private _updateSkinEnum() {
        const skinEnum = this._getSkinEnumSource();
        this._enumSkins = Enum({});
        Object.assign(this._enumSkins, skinEnum);
        Enum.update(this._enumSkins);
        setEnumAttr(this, '_skinIndex', this._enumSkins);
    }

    // 每次編輯器讀取列舉時檢查來源是否有變化，避免只在名稱為空或 setter 執行時才刷新，
    // 導致載入時 skinName/animationName 已經有效的實例一直停留在快取的 <None>。
    private _syncInspectorEnums() {
        if (!hasSameEnumEntries(this._getAnimEnumSource(), this._enumAnimations)) {
            this._updateAnimEnum();
        }
        if (!hasSameEnumEntries(this._getSkinEnumSource(), this._enumSkins)) {
            this._updateSkinEnum();
        }
    }
}
